import React, { useRef, useState } from 'react';
import { Card } from '../core/types';
import StackCardComponent from './StackCardComponent';
import '../styles/HandDisplay.css';

interface HandDisplayProps {
  handColumns: Card[][];
  selectedCardId: string | null;
  onCardClick: (cardId: string) => void;
  onCardDoubleClick?: (cardId: string) => void;
  isManualArrange?: boolean;
  onManualCardMove?: (cardId: string, targetColumnIndex: number) => void;
  disabled?: boolean;
}

const MAX_MANUAL_COLUMNS = 10;

const HandDisplay: React.FC<HandDisplayProps> = ({
  handColumns,
  selectedCardId,
  onCardClick,
  onCardDoubleClick,
  isManualArrange = false,
  onManualCardMove,
  disabled = false
}) => {
  const [draggedCardId, setDraggedCardId] = useState<string | null>(null);
  const pointerDrag = useRef<{ cardId: string; startX: number; startY: number; moved: boolean } | null>(null);
  const suppressClick = useRef(false);

  const moveDraggedCard = (cardId: string, targetColumnIndex: number) => {
    if (targetColumnIndex >= MAX_MANUAL_COLUMNS) return;
    onManualCardMove?.(cardId, targetColumnIndex);
    setDraggedCardId(null);
  };

  /**
   * 智能生成列顶全显标签（让玩家一目了然看清整列由哪些牌组成）
   */
  const getColumnBadge = (col: Card[]): { text: string; isHuxi: boolean } => {
    if (col.length === 0) return { text: '', isHuxi: false };

    // 1. 3张或4张相同 (坎 / 提)
    if (col.length >= 3) {
      const isAllSame = col.every(c => c.type === col[0].type && c.value === col[0].value);
      if (isAllSame) {
        return {
          text: `${col[0].name}坎 · ${col[0].isBig ? '6' : '3'}胡`,
          isHuxi: true
        };
      }

      // 壹贰叁 / 一二三
      const vals = col.map(c => c.value).sort((a, b) => a - b);
      const isSameType = col.every(c => c.type === col[0].type);
      if (isSameType && vals[0] === 1 && vals[1] === 2 && vals[2] === 3) {
        return {
          text: `${col[0].isBig ? '壹·贰·叁' : '一·二·三'} · ${col[0].isBig ? '6' : '3'}胡`,
          isHuxi: true
        };
      }

      // 贰柒拾 / 二七十
      if (isSameType && vals[0] === 2 && vals[1] === 7 && vals[2] === 10) {
        return {
          text: `${col[0].isBig ? '贰·柒·拾' : '二·七·十'} · ${col[0].isBig ? '6' : '3'}胡`,
          isHuxi: true
        };
      }

      // 普通同类型顺子
      if (isSameType && vals[1] === vals[0] + 1 && vals[2] === vals[1] + 1) {
        return {
          text: `${col.map(c => c.name).join('·')} (顺)`,
          isHuxi: false
        };
      }

      // 绞牌 (2大1小 或 1大2小)
      if (col.every(c => c.value === col[0].value)) {
        return {
          text: `${col.map(c => c.name).join('·')} (绞)`,
          isHuxi: false
        };
      }
    }

    // 2. 对子
    if (col.length === 2 && col[0].type === col[1].type && col[0].value === col[1].value) {
      return {
        text: `${col[0].name}·${col[1].name} (对)`,
        isHuxi: false
      };
    }

    // 3. 2张连续连牌
    if (col.length === 2 && col[0].type === col[1].type && Math.abs(col[0].value - col[1].value) <= 2) {
      return {
        text: `${col.map(c => c.name).join('·')} (连)`,
        isHuxi: false
      };
    }

    // 4. 其他散牌组合：直接列出所有字名，方便一目了然
    return {
      text: col.map(c => c.name).join('·'),
      isHuxi: false
    };
  };

  return (
    <div className={`hand-display-container ${isManualArrange ? 'manual-arrange' : ''}`}>
      <div
        className="hand-columns"
        style={{ '--hand-column-count': Math.min(Math.max(handColumns.length + (isManualArrange && handColumns.length < MAX_MANUAL_COLUMNS ? 1 : 0), 1), MAX_MANUAL_COLUMNS) } as React.CSSProperties}
      >
        {handColumns.length === 0 ? (
          <div className="empty-hand-prompt">手中暂无手牌</div>
        ) : (
          handColumns.map((col, colIdx) => {
            const badge = getColumnBadge(col);
            return (
              <div
                key={colIdx}
                className="hand-column"
                data-column-index={colIdx}
                onDragOver={event => {
                  if (isManualArrange) event.preventDefault();
                }}
                onDrop={event => {
                  event.preventDefault();
                  const cardId = event.dataTransfer.getData('text/plain') || draggedCardId;
                  if (cardId) moveDraggedCard(cardId, colIdx);
                }}
              >
                {/* 列顶全显徽章 */}
                {badge.text && (
                  <div className={`column-badge ${badge.isHuxi ? 'badge-huxi' : ''}`}>
                    {badge.text}
                  </div>
                )}

                {/* 纵向层叠卡牌列 (Hover时纵向手风琴展开) */}
                <div className="column-cards">
                  {col.map((card, cardIdx) => {
                    const isSelected = selectedCardId === card.id;

                    return (
                      <StackCardComponent
                        key={card.id}
                        card={card}
                        index={cardIdx}
                        total={col.length}
                        selected={isSelected}
                        disabled={disabled}
                        hideWatermark={true}
                        className={`column-card-item ${isSelected ? 'is-selected' : ''} ${draggedCardId === card.id ? 'is-dragging' : ''}`}
                        wrapperProps={{
                          onDragStart: event => {
                            if (!isManualArrange) return;
                            event.dataTransfer.setData('text/plain', card.id);
                            event.dataTransfer.effectAllowed = 'move';
                            setDraggedCardId(card.id);
                          },
                          onDragEnd: () => setDraggedCardId(null),
                          onPointerDown: event => {
                            if (!isManualArrange || event.pointerType === 'mouse') return;
                            pointerDrag.current = {
                              cardId: card.id,
                              startX: event.clientX,
                              startY: event.clientY,
                              moved: false
                            };
                            event.currentTarget.setPointerCapture(event.pointerId);
                          },
                          onPointerMove: event => {
                            const drag = pointerDrag.current;
                            if (!drag || drag.cardId !== card.id) return;
                            if (Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) > 8) {
                              drag.moved = true;
                              setDraggedCardId(card.id);
                            }
                          },
                          onPointerUp: event => {
                            const drag = pointerDrag.current;
                            pointerDrag.current = null;
                            if (!drag?.moved) return;
                            const target = document
                              .elementFromPoint(event.clientX, event.clientY)
                              ?.closest<HTMLElement>('[data-column-index]');
                            const targetIndex = Number(target?.dataset.columnIndex);
                            if (Number.isInteger(targetIndex)) moveDraggedCard(card.id, targetIndex);
                            suppressClick.current = true;
                            setDraggedCardId(null);
                          },
                          onDoubleClick: () => onCardDoubleClick?.(card.id),
                          draggable: isManualArrange,
                          title: isManualArrange ? '拖动到其他牌组，或拖到加号新建牌组' : undefined
                        }}
                        onClick={() => {
                          if (suppressClick.current) {
                            suppressClick.current = false;
                            return;
                          }
                          onCardClick(card.id);
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
        {isManualArrange && handColumns.length > 0 && handColumns.length < MAX_MANUAL_COLUMNS && (
          <button
            className="new-hand-column-target"
            data-column-index={handColumns.length}
            title="把选中的牌拆成新牌组，也可以拖到这里"
            disabled={!selectedCardId}
            onClick={() => selectedCardId && onManualCardMove?.(selectedCardId, handColumns.length)}
            onDragOver={event => event.preventDefault()}
            onDrop={event => {
              event.preventDefault();
              const cardId = event.dataTransfer.getData('text/plain') || draggedCardId;
              if (cardId) moveDraggedCard(cardId, handColumns.length);
            }}
          >
            ＋
          </button>
        )}
      </div>
    </div>
  );
};

export default HandDisplay;
